import { Configuration, FormField, FieldState, CardInformation, StyleChangeEventArgs, FieldEventArgs, FieldEventInfo, FieldEventType, FieldInfo, Style } from './types';
import { isPresent, mergeDeep, stringIsNullOrEmpty, isObject, valueIsNullOrEmpty, isIE10, getHostUrl } from './helpers';
import { PromiseResolver } from './promiseResolver';

const cardTypeLookup = {
    VISA: 'Visa',
    MASTERCARD: 'MasterCard',
    AMEX: 'Amex'
};

const StyleName = {
    default: 'default',
    success: 'success',
    error: 'error'
}

const errorMessages = {
    connectionFailureWarning: "Could not connect to Payment System",
    ie10TLSWarning: "Could not connect to Payment System. For Internet Explorer 10, please go to " +
                    "Tools -> Internet Options-> Advanced -> Security and enable TLS 1.1 and TLS 1.2"
};

/**
 * PaymentService client
 */
export class PaymentService { 
    private defaultTimeout = 10000;

    // Keep this in sync with enum FieldEventType
    fieldEventType = {
        blur: 'blur',
        focus: 'focus',
        stateChanged: 'stateChanged'
    };

    // Keep this in sync with enum FieldState
    fieldState = {
        empty: 'empty',
        valid: 'valid',
        invalid: 'invalid'
    };

    cardType = {
        Visa: 'Visa',
        MasterCard: 'MasterCard',
        Amex: 'Amex'
    }

    config: Configuration;
    private _defaultConfig: Configuration = {
        url: null,
        configTimeout: this.defaultTimeout,
        submitTimeout: this.defaultTimeout,
        styles: {
            default: {
                fontFamily: 'sans-serif',
                fontSize: '14px',
                fontWeight: 'normal',
                color: '#555555',
                padding: '0px 0px 0px 4px',
                tooltipCC: ''
            },
            success: {
                fontFamily: 'sans-serif',
                fontSize: '14px',
                fontWeight: 'normal',
                color: '#555555',         
                padding: '0px 0px 0px 4px',
                tooltipCC: ''
            },
            error: {
                fontFamily: 'sans-serif',
                fontSize: '14px',
                fontWeight: 'normal',
                color: 'red',
                padding: '0px 0px 0px 4px',
                tooltipCC: ''
            }                
        },
        classNames: {
            empty:      'psEmpty',
            invalid:    'psInvalid',
            valid:      'psValid',
            focus:      'psFocus',
            touched:    'psTouched',
            graceEnded: 'psGraceEnded'
        },
        cardNumber: {
            id: 'cardNumber',
            placeholder: 'Card number',
            maxLength: 19,
            defaultStyle: 'default'
        },
        securityCode: {
            id: 'securityCode',
            placeholder: 'Security code',
            maxLength: 4,
            defaultStyle: 'default'
        }
    };

    private _boundMessageHandler: (evt: MessageEvent) => void;
    private _configureResolver: PromiseResolver = null;
    private _submitResolver: PromiseResolver = null;
    private _fields: { [index: string] : FormField } = {};
    private _fieldNames = {
        cardNumber: 'cardNumber',
        securityCode: 'securityCode'
    };

    //#region -- Public Properties --

    /**
     * Gets whether the hosted fields are all valid
     */
    get isValid(): boolean {
        const result = Object.keys(this._fields)
            .map(fieldName => this._fields[fieldName])
            .every(p => p.state === FieldState.valid);

        return result;
    }

    //#endregion

    //#region -- Public Methods --

    /**
     * Initializes the payment service hosted fields with the specified configuration.
     * @param configuration - specify payment service configuration
     * @returns {Promise} - promise will resolve when complete otherwise error
     */
    public configure(configuration: Configuration): Promise<any> {
        this._configureResolver = null;

        return new Promise((resolve, reject) => {
            try {
                if (!isPresent(configuration)) throw new Error('configurtion parameter is required')
                if (!isPresent(configuration.url)) throw new Error('configuration.url is required');

                // Remove previous event listener - bind creates a new function hence needs to be stored.
                if (isPresent(this._boundMessageHandler)) {
                    window.removeEventListener('message', this._boundMessageHandler);
                }

                this.config = mergeDeep(this._defaultConfig, configuration);
                this.config.host = this.config.host || getHostUrl(this.config.url);
                this._initialiseField(this._fieldNames.cardNumber, this.config.cardNumber);
                this._initialiseField(this._fieldNames.securityCode, this.config.securityCode);

                this._configureResolver = new PromiseResolver(resolve, reject, this.config.configTimeout || this.defaultTimeout, this._configTimeout);

                // Hook up event listener for messages from OnePay
                this._boundMessageHandler = this._handleMessageEvents.bind(this);
                window.addEventListener('message', this._boundMessageHandler);
                
            } catch (error) {
                if (isPresent(this._configureResolver)) {
                    this._configureResolver.reject(error);
                    this._configureResolver = null;
                } else {
                    reject(error);
                }
            }        
        });
    }

    /**
     * Submit card information to OnePay
     * @param cardInformation - specify card information to post to OnePay
     * @returns {Promise} - promise will resolve when complete otherwise error
     */
    public submit(cardInformation: CardInformation): Promise<any> {
        this._submitResolver = null;
        return new Promise((resolve, reject) => {
            try {
                this.forceValidate();

                if (!isPresent(cardInformation)) {
                    let error = new Error('cardInformation parameter is required');
                    error.name = 'cardInformation';
                    throw error;
                }
                if (stringIsNullOrEmpty(cardInformation.cardHolderName)) {
                    let error = new Error('Cardholder name is required');
                    error.name = 'cardHolderName';
                    throw error;
                }
                if (valueIsNullOrEmpty(cardInformation.expiryMonth)) {
                    let error = new Error('Card Expiry month is required');
                    error.name = 'expiryMonth';
                    throw error;
                }
                if (valueIsNullOrEmpty(cardInformation.expiryYear)) {
                    let error = new Error('Card Expiry year is required');
                    error.name = 'expiryYear';
                    throw error;
                }
                if (!this.isValid) {
                    throw new Error('Card details are invalid');
                }

                const cardNumberFrame = this._fields[this._fieldNames.cardNumber].input.frame;
                
                if (!isPresent(cardNumberFrame)) throw new Error('Payment Service has not been configured');

                this._submitResolver = new PromiseResolver(resolve, reject, this.config.submitTimeout || this.defaultTimeout);

                // OnePay Message
                var message = {
                    type: 'doPayment',
                    payload: {
                        cardHolderName: cardInformation.cardHolderName,
                        cardHolderFirstName: null,
                        validity: {
                            month: cardInformation.expiryMonth,
                            year: cardInformation.expiryYear
                        }
                    }
                };

                // Post Message to OnePay
                cardNumberFrame.contentWindow.postMessage(message, this.config.host);
            } catch (error) {
                if (isPresent(this._submitResolver)) {
                    this._submitResolver.reject(error);
                    this._submitResolver = null;
                } else {
                    reject(error);
                }                    
            }
        });
    }

    /**
     * Sets the style for the specified field
     * @param id - the field identifier specified by configuration
     * @param styleName - the style name to set from list of styles
     */
    public setStyle(id: string, styleName: string): void {
        if (!isPresent(id) || !isPresent(styleName)) return;

        const formField = Object.keys(this._fields)
                            .map(fieldName => this._fields[fieldName])
                            .find(p => p.fieldInfo.id === id);

        if (isPresent(formField) && formField.style !== styleName) {
            const style = this.config.styles[styleName];

            if (isPresent(style)) {
                formField.style = styleName; // Set field current applied style
                this._setFormFieldStyle(formField, style as Style);
            }
        }                            
    }

    /**
     * Sets the hasValidated value to true for each field and updates class and styles
     */
    public forceValidate(): void {
        Object.keys(this._fields)
            .map(fieldName => this._fields[fieldName])
            .forEach(formfield => {
                formfield.hasValidated = true;
                this._setClass(formfield);
                this._doStyleChangeEvent(formfield);
            });
    }

    //#endregion

    //#region -- Private Methods --

    /**
     * Handles all messages from OnePay
     * @param evt - MessageEvent from OnePay
     */
    private _handleMessageEvents(evt: MessageEvent): void {
        if (evt.origin.indexOf(this.config.host) !== 0) {
            // Ignore messages from sources other than the OnePay hosted fields.
            return;
        }

        var eventInfo = evt.data;

        if (eventInfo.type === 'pageReady') {
            // Event is triggered when hosted field is ready
            this._handlePageReadyEvent(eventInfo);
        } else if (eventInfo.type === 'paymentStatus') {
            // Event is triggered when submit of cardInformation is completed
            this._handlePaymentStatus(eventInfo);
        } else if (eventInfo.type === 'cardTypeChange') {
            this._handleCardTypeChanged(eventInfo);
        } else {
            // Handle all other events
            this._handleFieldEvent(eventInfo);
        }
    }

    /**
     * Handles OnePay notification when hosted fields are finished setting up. 
     *  This will be fired (twice) for each field, but will only resolve the promise
     *  once all fields are initialized.
     * @param eventInfo - OnePay event data
     */
    private _handlePageReadyEvent(eventInfo: any): void {
        const formField: FormField = this._fields[eventInfo.field];
        
        if (!isPresent(formField)) {
            // Field not found so either reject or throw error
            const error = new Error('Field "' + eventInfo.field + '" not found');
            if (isPresent(this._configureResolver) && !this._configureResolver.isComplete) {
                this._configureResolver.reject(error);
                this._configureResolver = null;
                return;
            } else {
                throw error;
            }
        }

        formField.isInitialized = true;

        if (this._areAllFieldsInitialized()) {
            if (!isPresent(this._configureResolver)) {
                throw new Error('No configuration resolver');
            } else if (this._configureResolver.isComplete) {
                // Host Fields are ready after timeout has fired
                this._configureResolver = null;
                return;    
            }
            
            // Resolve Configure when all fields have successfully been initialized
            this._configureResolver.resolve(this); 
            this._configureResolver = null;
            this._showFields();
        }
    }

    /**
     * Handles OnePay notifications when cardInformation has been submitted via hosted field
     * @param eventInfo - OnePay event data
     */
    private _handlePaymentStatus(eventInfo: any): void {
        if (!isPresent(this._submitResolver)) {
            throw new Error('No submit resolver');
        }

        if (eventInfo.payload === 'Success') { 
            // Submit of hosted fields to OnePay was successful
            this._submitResolver.resolve(true);
        } else {
            this._submitResolver.reject(new Error('Card information is incomplete or submit was unsuccessful'));
        }
        this._submitResolver = null;
    }

    /**
     * Handles OnePay notifications when Card Type has changed
     * @param eventInfo - OnePay event data
     */
    private _handleCardTypeChanged(eventInfo: any): void {
        if (!isPresent(this.config.onCardTypeChangeEvent) || !isPresent(eventInfo)) {
            return;
        }

        this.config.onCardTypeChangeEvent({
            cardType: cardTypeLookup[eventInfo.payload.cardType] || 'Unknown',
            panMask: eventInfo.payload.firstDigits
        });
    }

    /**
     * Handles OnePay notifications for all other events
     * @param eventInfo - OnePay event data
     */
    private _handleFieldEvent(eventInfo: any): void {
        if (!isPresent(eventInfo) || !isPresent(eventInfo.field) || !isPresent(eventInfo.type)) {
            // Unexpected. Messages from OnePay always have a type and field property
            return;
        }

        const formField: FormField = this._fields[eventInfo.field];
        let psEvent: FieldEventArgs = null;

        switch (eventInfo.type) {
            case 'focus':
                formField.hasFocus = true;
                psEvent = { eventType: FieldEventType.focus };
                break;
            case 'blur':
                formField.hasValidated = true;
                formField.hasFocus = false;
                psEvent = { eventType: FieldEventType.blur };
                break;
            case 'empty':
                formField.state = FieldState.empty;
                psEvent = { eventType: FieldEventType.stateChanged };
                break;
            case 'notEmpty':
                formField.state = FieldState.invalid;
                psEvent = { eventType: FieldEventType.stateChanged };
                break;
            case 'validityChange':
                //eventInfo.payload is a true/false value
                if (eventInfo.payload) {
                    formField.hasValidated = true;
                    formField.state = FieldState.valid;
                }
                else {
                    formField.state = FieldState.invalid;
                }
                psEvent = { eventType: FieldEventType.stateChanged };
                break;
            default:
                psEvent = { eventType: eventInfo.type };
        }

        if (!isPresent(psEvent)) return;

        this._setClass(formField);

        // Notify FieldEvent changes
        if (isPresent(this.config.onFieldEvent)) {
            psEvent.field = {
                id: formField.fieldInfo.id,
                state: formField.state,
                hasFocus: formField.hasFocus,
                hasValidated: formField.hasValidated
            };
            setTimeout(() => { 
                this.config.onFieldEvent(psEvent); 
            }, 0);
        }

        // Notify FieldStyle Changed
        if (psEvent.eventType === FieldEventType.blur 
            || psEvent.eventType === FieldEventType.stateChanged) {
            this._doStyleChangeEvent(formField);
        }
        
    }

    private _doStyleChangeEvent(formField: FormField): void {
        let style = this._getFormFieldStyle(formField);

        const field = {
            id: formField.fieldInfo.id,
            state: formField.state,
            hasFocus: formField.hasFocus,
            hasValidated: formField.hasValidated
        };

        if (isPresent(this.config.onStyleChangeEvent)) {
            // Call event with callback
            let styleEventArgs: StyleChangeEventArgs = {
                field: field,
                currentStyle: formField.style,
                newStyle: style,
                styleCallback: this.setStyle.bind(this)
            };
            setTimeout(() => {
                this.config.onStyleChangeEvent(styleEventArgs);
            }, 0);
        } else {
            this.setStyle(formField.fieldInfo.id, style);
        }        
    }

    /**
     * Show all hosted fields once all fields have finsihed being initialised.
     */
    private _showFields(): void {
        Object.keys(this._fields)
            .map(fieldName => this._fields[fieldName])
            .forEach(formField => {
                formField.input.frame.style.display = 'block';
                this._setClass(formField);
            });
    }

    /**
     * Set the Input Container Field classes based on field form state
     * @param formField - form field
     */
    private _setClass(formField: FormField) {
        //IE 10+ supports classList
        switch (formField.state) {
            case FieldState.empty:
                formField.input.parent.classList.add(this.config.classNames.empty);
                formField.input.parent.classList.remove(this.config.classNames.invalid);
                formField.input.parent.classList.remove(this.config.classNames.valid);
                break;
            case FieldState.invalid:
                formField.input.parent.classList.add(this.config.classNames.invalid);
                formField.input.parent.classList.remove(this.config.classNames.valid);
                formField.input.parent.classList.remove(this.config.classNames.empty);
                break;
            case FieldState.valid:
                formField.input.parent.classList.add(this.config.classNames.valid);
                formField.input.parent.classList.remove(this.config.classNames.invalid);
                formField.input.parent.classList.remove(this.config.classNames.empty);
                break;
            default:
                break;
        }

        if (formField.hasValidated) {
            formField.input.parent.classList.add(this.config.classNames.graceEnded); // Is this needed?
        }

        if (formField.hasFocus) {
            formField.input.parent.classList.add(this.config.classNames.focus);
            formField.input.parent.classList.add(this.config.classNames.touched);
        } else {
            formField.input.parent.classList.remove(this.config.classNames.focus);
        }
    }

    /**
     * Sets the formfield style of the hosted field
     * @param formField - form field
     * @param style - style
     */
    private _setFormFieldStyle(formField: FormField, style: Style): void {
        if (!isPresent(formField) || !isPresent(style)) return;
        var data = {
            type: 'style',
            payload: style
        };
        formField.input.frame.contentWindow.postMessage(data, this.config.host);             
    }

    /**
     * Returns the style to be applied based on the state of the form field
     * @param formField - form field
     * @returns {string} - Style 
     */
    private _getFormFieldStyle(formField: FormField): string {
        let style: string;
        
        if (!formField.hasValidated) {
            style = StyleName.default;
        } else if (formField.state === FieldState.invalid) {
            style = StyleName.error
        } else {
            style = StyleName.success;
        }

        return style;
    }

    /**
     * Gets the HtmlElement based on identifier
     * @param id - the field identifier passed from configuration
     */
    private _getFieldParent(id: string) {
        var parentField = document.getElementById(id);

        if (!isPresent(parentField)) {
            throw new Error('Field "' + id + '" not found');
        }

        return parentField;
    }       

    /**
     * Handles the result of the configure timeout. This will happen when the 'pageReady' event 
     * for hosted fields has not been received.
     * @param value - PromiseResolver
     */
    private _configTimeout(value: PromiseResolver): void {
        const errorMsg = isIE10() ? errorMessages.ie10TLSWarning : errorMessages.connectionFailureWarning;
        value.reject(new Error(errorMsg));
        //this.configureResolver = null;
    }

    /**
     * Initialize the hosted field
     * @param formName - form field name
     * @param fieldInfo - field info, take from configuration
     */
    private _initialiseField(fieldName: string, fieldInfo: FieldInfo): void {
        let formField: FormField = { 
            fieldName: fieldName,
            fieldInfo: null,
            isInitialized: false,
            state: FieldState.empty, 
            style: null,
            hasFocus: false, 
            hasValidated: false,
            input: null
        };
        
        this._fields[fieldName] = formField;

        formField.style = fieldInfo.defaultStyle; // Style is initially applied via buildUrl
        formField.fieldInfo = fieldInfo;
        formField.input = {
            frame: document.createElement('iframe'),
            parent: this._getFieldParent(fieldInfo.id)
        };
        
        formField.input.frame.style.display = 'none'; // Don't display until ready event is received
        formField.input.parent.innerHTML = ''; // Clear any content inside of div
        formField.input.parent.appendChild(formField.input.frame); // Add iFrame to div

        var url = this._buildUrl(formField.fieldName, fieldInfo);
        formField.input.frame.src = url;
    }

    /**
     * Build url for host field iframe
     * @param fieldType - fieldType cardNumber or securityCode as defined by OnePay
     * @param fieldInfo - field info taken from configuration
     * @param style - initialise with the default style
     */
    private _buildUrl(fieldType: string, fieldInfo: FieldInfo, style: Style = null): string {
        var url = this.config.url + '&type=' + encodeURIComponent(fieldType);
        if (!isPresent(style)) {
            style = this.config.styles[fieldInfo.defaultStyle];
        }
        if (isPresent(fieldInfo.placeholder)) {
            url += '&placeholder=' + encodeURIComponent(fieldInfo.placeholder);
        }
        if (isPresent(fieldInfo.maxLength)) {
            url += '&maxLength=' + encodeURIComponent(fieldInfo.maxLength.toString());
        }
        if (isPresent(style)) {
            if (isPresent(style.fontFamily)) {
                url += '&fontFamily=' + encodeURIComponent(style.fontFamily);
            }
            if (isPresent(style.fontSize)) {
                url += '&fontSize=' + encodeURIComponent(style.fontSize);
            }
            if (isPresent(style.fontWeight)) {
                url += '&fontWeight=' + encodeURIComponent(style.fontWeight);
            }
            if (isPresent(style.color)) {
                url += '&color=' + encodeURIComponent(style.color);
            }
            if (isPresent(style.padding)) {
                url += '&padding=' + encodeURIComponent(style.padding);
            }
            if (isPresent(style.tooltipCC)) {
                url += '&title=' + encodeURIComponent(style.tooltipCC);
            }
        }
        return url;
    }
    
    /**
     * Returns whether all the hosted fields have been initialised.
     */
    private _areAllFieldsInitialized(): boolean {
        var result = Object.keys(this._fields)
            .map(fieldName => this._fields[fieldName])
            .every(p => p.isInitialized);
        return result;
    }        
    
    //#endregion
}



// WEBPACK FOOTER //
// ./src/paymentService.ts